import React from "react";
import styled, { css } from "styled-components";

/**
 * A compatibility shim for MUI v4's `<Grid container>`/`<Grid item>` API,
 * covering the subset actually used in this app (container/item/spacing/
 * direction/xs/sm column spans). Not a general-purpose replacement for
 * MUI's Grid - just enough to avoid rewriting every call site by hand.
 */
export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  container?: boolean;
  item?: boolean;
  spacing?: number;
  direction?: "row" | "column";
  justifyContent?: React.CSSProperties["justifyContent"];
  alignItems?: React.CSSProperties["alignItems"];
  xs?: number;
  sm?: number;
}

const GRID_SM_BREAKPOINT = "600px";

const columnWidth = (span?: number) =>
  span == null ? undefined : `${(span / 12) * 100}%`;

export const Grid = styled.div<GridProps>`
  ${(props) =>
    props.container &&
    css`
      display: flex;
      flex-direction: ${props.direction ?? "row"};
      flex-wrap: wrap;
      box-sizing: border-box;
      /* Match MUI's negative-margin approach rather than a flex "gap": a
         gap adds to the total row width on top of item flex-basis
         percentages that already sum to 100%, pushing items to wrap.
         "spacing" is only ever set on the container, not on each item, so
         the item's own padding is applied here via a nested selector
         targeting direct Grid children (same generated class) rather than
         relying on each item receiving its own "spacing" prop. */
      margin: -${((props.spacing ?? 0) * 8) / 2}px;
      width: calc(100% + ${(props.spacing ?? 0) * 8}px);
      ${props.justifyContent && `justify-content: ${props.justifyContent};`}
      ${props.alignItems && `align-items: ${props.alignItems};`}

      & > & {
        padding: ${((props.spacing ?? 0) * 8) / 2}px;
      }
    `}

  ${(props) =>
    props.item &&
    css`
      box-sizing: border-box;
      ${props.xs != null &&
      css`
        flex-basis: ${columnWidth(props.xs)};
        max-width: ${columnWidth(props.xs)};
      `}

      @media (min-width: ${GRID_SM_BREAKPOINT}) {
        ${props.sm != null &&
        css`
          flex-basis: ${columnWidth(props.sm)};
          max-width: ${columnWidth(props.sm)};
        `}
      }
    `}
`;

export default Grid;
